import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BookOpenCheck, Microscope, ShieldCheck } from "lucide-react";
import { InkCharacter } from "@/components/InkCharacter";

const BENEFITS = [
  [Microscope, "Explore pigment options with clear explanations."],
  [BookOpenCheck, "Understand the evidence behind each shade."],
  [ShieldCheck, "Make the final decision with greater confidence."],
];

export const BrandLockup = ({ center = false, className = "" }) => (
  <img src={`${process.env.PUBLIC_URL || ""}/ink-genius-wordmark.webp`} alt="Ink Genius — AI-Powered Pigment Intelligence by MooreBeautyLove"
    data-testid="brand-lockup" draggable={false}
    className={`h-11 w-auto select-none object-contain sm:h-16 ${center ? "mx-auto" : ""} ${className}`} />
);

export default function WelcomePage() {
  const { user, logout } = useAuth();
  return (
    <div className="ig-grain relative min-h-screen overflow-hidden bg-[#FDFBF7]">
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#FDF2F0]" />
      <div className="relative mx-auto flex min-h-screen max-w-5xl flex-col px-6 py-8 sm:px-10 lg:flex-row lg:items-center lg:gap-16">
        <div className="ig-rise flex flex-col items-center pt-2 lg:w-2/5 lg:pt-0">
          <InkCharacter size="xl" />
        </div>
        <div className="mt-10 flex-1 lg:mt-0">
          <h1 className="ig-rise ig-d1 text-4xl font-semibold leading-[1.05] text-[#2C1810] sm:text-5xl lg:text-6xl" data-testid="welcome-headline">
            Meet your pigment intelligence partner.
          </h1>
          <p className="ig-rise ig-d2 mt-5 max-w-xl text-base leading-relaxed text-[#4A3B32]">
            Make more informed pigment decisions with science-informed guidance built around your technique, your client, and the pigments in your kit.
          </p>
          <ul className="ig-rise ig-d3 mt-8 space-y-4" data-testid="welcome-benefits">
            {BENEFITS.map(([Icon, text]) => (
              <li key={text} className="flex items-center gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FDF2F0] text-[#2C1810] ring-1 ring-[#E8BDB5]"><Icon size={18} strokeWidth={1.6} /></span>
                <span className="text-base text-[#2C1810]">{text}</span>
              </li>
            ))}
          </ul>
          {user ? (
            <div className="ig-rise ig-d4 mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link to="/subscribe" data-testid="welcome-back-to-subscribe-btn" className="inline-flex min-h-[52px] shrink-0 whitespace-nowrap items-center justify-center gap-2 rounded-full bg-[#C5A059] px-8 text-sm font-semibold tracking-wide text-[#2C1810] transition-colors hover:bg-[#d4b273]">
                <ArrowLeft size={16} /> Back to subscribe
              </Link>
              <p className="text-center text-sm text-[#6E5549] sm:text-left">
                Signed in as {user.email} ·{" "}
                <button onClick={logout} data-testid="welcome-sign-out-btn" className="font-semibold text-[#2C1810] underline decoration-[#C5A059] underline-offset-4">Sign out</button>
              </p>
            </div>
          ) : (
          <div className="ig-rise ig-d4 mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link to="/signup" data-testid="welcome-create-account-btn" className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-[#2C1810] px-8 text-sm font-semibold tracking-wide text-[#FDFBF7] transition-colors hover:bg-[#3D2317] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C5A059]">
              Create My Account
            </Link>
            <p className="text-center text-sm text-[#6E5549] sm:text-left">
              Already have an account?{" "}
              <Link to="/signin" data-testid="welcome-sign-in-link" className="font-semibold text-[#2C1810] underline decoration-[#C5A059] underline-offset-4">Sign in</Link>
            </p>
          </div>
          )}
          <Link to="/pricing" data-testid="welcome-pricing-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#9C6218] underline decoration-[#C5A059]/60 underline-offset-4 hover:text-[#2C1810]">
            See plans & pricing · 7-day free trial
          </Link>
          <p className="mt-10 font-mono-ig text-[11px] uppercase tracking-[0.3em] text-[#9C6218]">Science-Informed. Artist-Driven.</p>
        </div>
      </div>
    </div>
  );
}
